import React, { useState } from 'react';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { lobIdentifierRegex } from '~/components/ClaimPage/constants';
import { MainCard, Text } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import type { DndDragHandleProps } from '~/components/core/DndOrderedList/DndOrderedList.types';
import FsTooltip from '~/components/core/FsWrappers/FsTooltip/FsTooltip';
import TextField from '~/components/core/Molecules/Fields/TextField';
import TextWithChips from '~/components/core/TextWithChips';
import TextFieldFormik from '~/components/TextFieldFormik';
import Switch from '~/components/ui/atoms/Switch';
import cn from '~/Utils/cn';

import { getCatalogNamespace } from '../claimBarFieldKey';
import { BAR_TEXTS as TEXTS } from '../texts';
import type { ClaimBarFieldEntry } from '../types';

import ClaimBarPill from './ClaimBarPill';

/** Used to prevent card state from toggling when clicking controls in the "title" area */
const stop = (e: React.MouseEvent<HTMLElement>) => {
  e.stopPropagation();
};

/**
 * Formik reads a dot in a field path as a step into a nested object, and `TextFieldFormik` uses the
 * `id` it is given as that path. A catalog-path key such as `claim.api.insurer_name` would bind the
 * input to `values.claim_bar_config_claim.api...` while `initialValues`, the validation schema and
 * `onSubmit` all address the entry flatly - the input renders empty and Save posts the display name
 * back unchanged. The key still has to reach the id, because the id is also the DOM id and has to
 * stay unique per card; `-` cannot occur in a field key (the fixed keys and every segment of the
 * catalog-path pattern are `[a-z0-9_]`), so swapping it in keeps distinct keys distinct.
 */
const getFieldId = (field_key: string, type: 'name' | 'key') =>
  `claim_bar_config_${field_key.replaceAll('.', '-')}_${type}`;

type HeldNameFieldProps = {
  id: string;
  displayName: string;
  helperText: string;
  disabled: boolean;
  onDisplayNameChange: (displayName: string) => void;
};

/**
 * The name input of a screen with one Save: each keystroke is handed on as it is typed, and the screen holds it
 * trimmed. The input keeps the text as typed, so a space between words survives the trim; a held name that changes
 * from elsewhere (a discard, a reset, a save read back) replaces it.
 */
const HeldNameField = ({ id, displayName, helperText, disabled, onDisplayNameChange }: HeldNameFieldProps) => {
  const [typed, setTyped] = useState(displayName);
  const [heldName, setHeldName] = useState(displayName);
  if (displayName !== heldName) {
    setHeldName(displayName);
    if (displayName !== typed.trim()) {
      setTyped(displayName);
    }
  }
  const isBlank = typed.trim() === '';

  return (
    <TextField
      id={id}
      label={TEXTS.displayNameLabel}
      value={typed}
      onChange={(value: string) => {
        setTyped(value);
        onDisplayNameChange(value);
      }}
      error={isBlank}
      helperText={isBlank ? TEXTS.displayNameRequired : helperText}
      disabled={disabled}
    />
  );
};

type BarFieldConfigCardProps = {
  field: ClaimBarFieldEntry;
  dragHandleProps: DndDragHandleProps;
  onToggleEnabled: () => void;
  onDisplayNameChange: (displayName: string) => void;
  disabled: boolean;
  /**
   * Locks the enable toggle alone, leaving the rest of the card editable. Kept separate from
   * `disabled`, which locks the whole card while a save is in flight.
   */
  isToggleLocked: boolean;
  /** Tooltip explaining why the toggle is locked. Rendered only while `isToggleLocked`. */
  toggleLockReason?: string;
  /** Greys the title of a field that is switched off. */
  dimWhenHidden?: boolean;
  /**
   * Hands the name on as it is typed, for a screen whose one Save sends every held change, so the card has no Save
   * of its own. Without it the card saves the name itself, from its own Save.
   */
  holdsTypedName?: boolean;
  /**
   * Lets the title take only the space the controls leave. MainCard's title wrapper is `w-full`, which
   * otherwise squeezes the controls and wraps their labels.
   */
  fitTitleToControls?: boolean;
};

const BarFieldConfigCard = ({
  field,
  dragHandleProps,
  onToggleEnabled,
  onDisplayNameChange,
  disabled,
  isToggleLocked,
  toggleLockReason,
  dimWhenHidden = false,
  holdsTypedName = false,
  fitTitleToControls = false,
}: BarFieldConfigCardProps) => {
  const { field_key, display_name, is_enabled } = field;
  const namespace = getCatalogNamespace(field_key);

  const title =
    field_key === 'policy_number' ? (
      <span className="pointer-events-none">
        {/* TextWithChips stops propagation of click events. Setting pointer-events-none to bypass this behavior */}
        <TextWithChips
          text={display_name.replace(lobIdentifierRegex, '{lob}')}
          tokensValues={{ lob: TEXTS.lobTokenDisplay }}
        />
      </span>
    ) : (
      display_name
    );

  const titleSlot = (
    <span className="inline-flex flex-wrap items-center gap-8">
      <Text
        weight="semi-bold"
        colorVariant={dimWhenHidden && !is_enabled ? Text.COLOR_VARIANTS.SECONDARY : Text.COLOR_VARIANTS.PRIMARY}
      >
        {title}
      </Text>
      {namespace === 'configured_fields' ? <ClaimBarPill label={TEXTS.configuredBadge} /> : null}
    </span>
  );

  const toggleControl = (
    <label className={cn('me-12 flex items-center', isToggleLocked ? 'cursor-default' : 'cursor-pointer')}>
      <Switch checked={is_enabled} onChange={onToggleEnabled} disabled={disabled || isToggleLocked} />
      <Text variant="small" weight="regular" colorVariant="black" className="ms-8 inline-block">
        {TEXTS.enableField}
      </Text>
    </label>
  );

  const actionSlot = (
    <div className="flex cursor-default items-center" onClick={stop}>
      {isToggleLocked ? (
        // Wrapping the whole label, not just the switch, so the tooltip is reachable from the "Show"
        // text too — a disabled switch swallows the pointer events a tooltip needs.
        <FsTooltip arrow title={toggleLockReason}>
          <span>{toggleControl}</span>
        </FsTooltip>
      ) : (
        toggleControl
      )}

      <button
        {...dragHandleProps}
        onClick={stop}
        className="flex cursor-grab items-center border-none bg-transparent p-0 text-slate-500 outline-none"
      >
        <DragIndicatorIcon />
      </button>
    </div>
  );

  const displayNameId = getFieldId(field_key, 'name');
  const fieldKeyId = getFieldId(field_key, 'key');

  const initialValues = {
    [displayNameId]: display_name,
    [fieldKeyId]: field_key,
  };

  const helperText = field_key === 'policy_number' ? TEXTS.policyNumberHelperText : '';

  return (
    <MainCard
      type="filled"
      title={titleSlot}
      titleAction={actionSlot}
      titleClassName={fitTitleToControls ? 'w-auto min-w-0 flex-1' : undefined}
      collapsible
    >
      {holdsTypedName ? (
        <div className="grid grid-cols-2 gap-20 px-16">
          <HeldNameField
            id={displayNameId}
            displayName={display_name}
            helperText={helperText}
            disabled={disabled}
            onDisplayNameChange={onDisplayNameChange}
          />
          <TextField id={fieldKeyId} label={TEXTS.fieldKeyLabel} value={field_key} disabled />
        </div>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={Yup.object().shape({
            [displayNameId]: Yup.string().trim().required(TEXTS.displayNameRequired),
          })}
          onSubmit={({ [displayNameId]: displayName }) => onDisplayNameChange(displayName)}
        >
          {({ resetForm }) => (
            <Form className="grid grid-cols-2 gap-20 px-16">
              <TextFieldFormik
                id={displayNameId}
                name={displayNameId}
                label={TEXTS.displayNameLabel}
                helperText={helperText}
                disabled={disabled}
              />
              <TextFieldFormik id={fieldKeyId} name={fieldKeyId} label={TEXTS.fieldKeyLabel} disabled />
              <div className="col-start-2 justify-self-end">
                <Button
                  variant="outlined"
                  color="primary"
                  className="mr-16"
                  onClick={() => resetForm()}
                  disabled={disabled}
                >
                  {TEXTS.cancel}
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={disabled}>
                  {TEXTS.save}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </MainCard>
  );
};

export default BarFieldConfigCard;
